import React, { useState, useEffect } from 'react';
import {
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  TableSortLabel,
  Button,
  Grid2,
  TextField,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import prevIcon from '../../assets/icons/left-arrow.svg';
import nextIcon from '../../assets/icons/right-arrow.svg';
import Navbar from '../navbar/Navbar';
import Sidemenu from '../sidemenu/Sidemenu';
import TokenError from '../tokenError';
import config from '../../config/config.js';
import {makeGetRequestToUrl} from '../commonFunc.jsx';
import constants from '../../constant.js'

const DynamicRidesPanel = () => {
    
    const serverUrl = config.serverUrl;
    const { status } = useParams();
    const navigate = useNavigate();

    const [columns, setColumns] = useState([]);
    const [rows, setRows] = useState([]);
    const [filters, setFilters] = useState({});
    const [filterValues, setFilterValues] = useState({});
    const [startDate, setStartDate] = useState(dayjs().subtract(7, 'day')); // Default: 7 days ago
    const [endDate, setEndDate] = useState(dayjs()); // Default: Today
    const [orderBy, setOrderBy] = useState('');
    const [order, setOrder] = useState('asc');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [tokenExpired, setTokenExpired] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [filteredRows, setFilteredRows] = useState([]);
    const [paginatedData, setPaginatedData] = useState([]);

    // Map statuses to human-readable names
    const reportNames = {
        ongoing: 'Ongoing Rides',
        completed: 'Completed Rides',
        cancelled: 'Cancelled Rides',
        timeout: 'Missed Rides'
      };
    
    const reportName = reportNames[status] || 'Rides Report';
  

    // Reset startDate and endDate when switching tabs
    useEffect(() => {
        setStartDate(dayjs().subtract(7, 'day')); // Reset to 7 days ago
        setEndDate(dayjs()); // Reset to today
    }, [status]);

    // Fetch data dynamically from API
    useEffect(() => {
        const fetchData = async () => {
        try {
            const token = localStorage.getItem('token');
            const timestamp = new Date().getTime();

            const statusMapping = {
                ongoing: 1,
                completed: 2,
                cancelled: 3,
                timeout: 4
            };

            const selectedStatus = statusMapping[status] || 1;

            const url = `${serverUrl}/customer/fetch_trip_engagement`;

            // Prepare API payload
            const obj = {
                token,
                timestamp,
                start_date: startDate.format('YYYY-MM-DD'), // Format as needed by your API
                end_date: endDate.format('YYYY-MM-DD'),
                status: selectedStatus
            };

            const response =  await makeGetRequestToUrl(url, obj);

            if(response.flag==102){
                console.log('Token is expired. Please login again')
                localStorage.removeItem('token');
                setErrorMessage('Your session has expired. Please log in again.');
                setTokenExpired(true);

                setTimeout(() => {
                    navigate('/signup');
                }, 2000);
            }

            const data = response.data;
            if (data && data.trip_engagement_details) {
            // Map API response to columns
            const columns = [
                { id: 'engagement_id', label: 'Engagement ID' },
                { id: 'request_id', label: 'Request ID' },
                { id: 'user_id', label: 'User ID' },
                { id: 'driver_id', label: 'Driver ID' },
                { id: 'client_vehicle_id', label: 'Client Vehicle ID' },
                { id: 'vehicle_type', label: 'Vehicle Type' },
                { id: 'status', label: 'Status' },
                { id: 'payment_mode', label: 'Payment Mode'},
                { id: 'pickup_latitude', label: 'Pickup Latitude' },
                { id: 'pickup_longitude', label: 'Pickup Longitude' },
                { id: 'pickup_address', label: 'Pickup Address' },
                { id: 'drop_latitude', label: 'Drop Latitude' },
                { id: 'drop_longitude', label: 'Drop Longitude' },
                { id: 'drop_address', label: 'Drop Address' },
                { id: 'ride_distance', label: 'Ride Distance (km)' },
                { id: 'ride_time', label: 'Ride Time (min)' },
                { id: 'estimated_fare', label: 'Estimated Fare' },
                { id: 'pickup_time', label: 'Pickup Time' },
                { id: 'drop_time', label: 'Drop Time' },

                { id: 'total_fare', label: 'Total Fare' },
                { id: 'driver_fare', label: 'Driver Fare' },
                { id: 'distance_fare', label: 'Distance Fare' },
                { id: 'time_fare', label: 'Time Fare' },
                { id: 'commission', label: 'Commission' },
                { id: 'platform_fee', label: 'Platform Fee' },
                { id: 'toll_charge', label: 'Toll Charge' },
                { id: 'waiting_charges', label: 'Waiting Charges' },
                { id: 'waiting_charges_driver_cut', label: 'Waiting Charges Driver Cut' },
                { id: 'luggage_charges', label: 'Luggage Charges' },
                { id: 'convenience_fee', label: 'Convenince Fee' },
                { id: 'convenience_driver_cut', label: 'Convenince Driver Cut' },
                { id: 'pickup_distance_charges', label: 'Pickup Distance Charges' },
                { id: 'pickup_distance_charges_driver_cut', label: 'Pickup Distance Charges Driver Cut' },
                { id: 'tax', label: 'Tax' },
                { id: 'created_at', label: 'Created At' },
            ];
    
            // Map rows from API response
            const rows = data.trip_engagement_details.map((item) => ({
                engagement_id: item.engagement_id,
                request_id: item.request_id,
                user_id: item.user_id,
                driver_id: item.driver_id,
                client_vehicle_id: item.client_vehicle_id,
                vehicle_type: item.vehicle_type,
                status: item.status,
                payment_mode: constants.paymentMode[item.payment_mode || 1],
                pickup_latitude: parseFloat(item.pickup_latitude).toFixed(6),
                pickup_longitude: parseFloat(item.pickup_longitude).toFixed(6),
                pickup_address: item.pickup_address,
                drop_latitude: parseFloat(item.drop_latitude).toFixed(6),
                drop_longitude: parseFloat(item.drop_longitude).toFixed(6),
                drop_address: item.drop_address,
                ride_distance: parseFloat(item.ride_distance).toFixed(2),
                ride_time: parseFloat(item.ride_time).toFixed(2),
                estimated_fare: parseFloat(item.estimated_fare).toFixed(2),
                pickup_time: item.pickup_time ? new Date(item.pickup_time).toLocaleString() : 'N/A',
                drop_time: item.drop_time ? new Date(item.drop_time).toLocaleString() : 'N/A',

                total_fare: item.total_fare,
                driver_fare: item.driver_fare,
                distance_fare: item.distance_fare,
                time_fare: item.time_fare,
                commission: item.commission,
                platform_fee: item.platform_fee,
                toll_charge: item.toll_charge,
                waiting_charges: item.waiting_charges,
                waiting_charges_driver_cut: item.waiting_charges_driver_cut,
                luggage_charges: item.luggage_charges,
                convenience_fee: item.convenience_fee,
                convenience_driver_cut: item.convenience_driver_cut,
                pickup_distance_charges: item.pickup_distance_charges,
                pickup_distance_charges_driver_cut: item.pickup_distance_charges_driver_cut,
                tax: item.tax,
                created_at: new Date(item.created_at).toLocaleString(),
            }));
    
            setColumns(columns);
            setRows(rows);
            setFilteredRows(rows);
            setPage(0);
            }
        } catch (error) {
            if (error.response?.status === 401) {
            setTokenExpired(true);
            } else {
            console.error('Error fetching data:', error);
            }
        }
        };

        fetchData();
    }, [startDate, endDate, status]);

    const handleRequestSort = (property) => {
      const isAscending = orderBy === property && order === 'asc';
      setOrder(isAscending ? 'desc' : 'asc');
      setOrderBy(property);
      const sortedRows = [...filteredRows].sort((a, b) => {
      if (a[property] < b[property]) return isAscending ? -1 : 1;
      if (a[property] > b[property]) return isAscending ? 1 : -1;
      return 0;
      });
      // setRows(sortedRows);
      setFilteredRows(sortedRows);
  };
  useEffect(() => {
    const paginated = Array.isArray(filteredRows)
        ? filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        : [];
    setPaginatedData(paginated);
}, [filteredRows, page, rowsPerPage]);

    const handleFilterChange = (key, value) => {
        setFilterValues((prev) => ({ ...prev, [key]: value }));
    };

    const handleChangePage = (newPage) => setPage(newPage);

    const sortedRows = [...rows].sort((a, b) => {
        if (!orderBy) return 0;
        return (a[orderBy] < b[orderBy] ? -1 : 1) * (order === 'asc' ? 1 : -1);
    });

    const handleExportCsv = () => {
        const headers = columns.map((col) => col.label).join(',');
        const csvRows = rows.map((row) =>
        columns.map((col) => `"${row[col.id] || ''}"`).join(',')
        );
        const csvContent = [headers, ...csvRows].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${reportName}.csv`;
        link.click();
    };

  return (
    <div>
      {tokenExpired && <TokenError />}

      
        <Typography style={{ fontSize: '28px', fontWeight: '500', marginBottom: '20px' }}>{reportName}</Typography>

        {/* Dynamic Filters */}
        <Grid2 container spacing={2} alignItems="center" style={{ marginBottom: '20px' }}>
          {/* Start Date Filter */}
          <Grid2 item>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                renderInput={(params) => <TextField {...params} style={{ width: '200px' }} />}
              />
            </LocalizationProvider>
          </Grid2>
          {/* End Date Filter */}
          <Grid2 item>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                renderInput={(params) => <TextField {...params} style={{ width: '200px' }} />}
              />
            </LocalizationProvider>
          </Grid2>
          {/* Export CSV Button */}
          <Grid2 item>
            <Button
              variant="contained"
              style={{
                marginLeft: '16px',
                backgroundColor: '#0065FF',
                color: 'white',
                height: '50px',
              }}
              onClick={handleExportCsv}
            >
              Export CSV
            </Button>
          </Grid2>
        </Grid2>


        {/* Data Table */}
        <TableContainer className="table-container" style={{ maxHeight: '700px', overflow: 'auto'  }}>
          <Table stickyHeader className="scrollable-table">
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={col.id} align="center" className="table-header" style={{ width: 'auto' }}>
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? order : 'asc'}
                      onClick={() => handleRequestSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row, index) => (
                <TableRow key={index}>
                  {columns.map((col) => (
                    <TableCell key={col.id} align="center">
                      {row[col.id] || 'N/A'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          <div className="custom-pagination">
              <div className="pagination-info">
                  {`Showing ${page * rowsPerPage + 1} to ${Math.min(
                  (page + 1) * rowsPerPage,
                  filteredRows.length
                  )} of ${filteredRows.length} entries`}
              </div>
              <div className="pagination-controls">
                  <button
                  onClick={() => handleChangePage(page - 1)}
                  disabled={page === 0}
                  className="pagination-arrow"
                  >
                  <img src={prevIcon} alt="Previous" />
                  </button>
                  <div className="pagination-numbers">
                  {page > 1 && (
                      <>
                      <button onClick={() => handleChangePage(0)} className="pagination-number">
                          1
                      </button>
                      <span className="pagination-dots">...</span>
                      </>
                  )}
                  {[...Array(Math.ceil(filteredRows.length / rowsPerPage))]
                      .map((_, index) => index)
                      .filter((index) => index >= page - 1 && index <= page + 1) // Show only current, previous, and next pages
                      .map((index) => (
                      <button
                          key={index}
                          onClick={() => handleChangePage(index)}
                          className={`pagination-number ${page === index ? 'active' : ''}`}
                      >
                          {index + 1}
                      </button>
                      ))}
                  {page < Math.ceil(filteredRows.length / rowsPerPage) - 2 && (
                      <>
                      <span className="pagination-dots">...</span>
                      <button
                          onClick={() => handleChangePage(Math.ceil(filteredRows.length / rowsPerPage) - 1)}
                          className="pagination-number"
                      >
                          {Math.ceil(filteredRows.length / rowsPerPage)}
                      </button>
                      </>
                  )}
               </div>
                  <button
                  onClick={() => handleChangePage(page + 1)}
                  disabled={page >= Math.ceil(filteredRows.length / rowsPerPage) - 1}
                  className="pagination-arrow"
                  >
                  <img src={nextIcon} alt="Next" />
                  </button>
              </div>
          </div>
        </TableContainer>
        
    </div>
  );
};

export default DynamicRidesPanel;